import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { ItemEntity } from './item.entity';
import { WarehouseEntity } from './warehouse.entity';

@Index('fk_warehouse__id', ['fkWarehouseId'], {})
@Index(
	'item_warehouse_fkItemId_deletedUserId_fkWarehouseId',
	['deletedUserId', 'fkItemId', 'fkWarehouseId'],
	{},
)
@Index(
	'item_warehouse_quantity_index',
	['fkItemId', 'fkWarehouseId', 'quantityOnHand'],
	{},
)
// PD-17025: the inventory reports find the rows that hold stock by a range on
// quantityOnHand, so they need an index that LEADS with it. The index above
// cannot be relied on for that: tenants created by database sync have it in
// the order declared here (item first), while older tenants carry an index of
// the same name ordered (quantityOnHand, fk_warehouse__id, fk_item__id).
// Database sync treats an index as present when either its name or its exact
// column list already exists, so this one is created only on the tenants that
// lack the quantity-first order and is skipped on the ones that have it.
// Because the same name means different columns on different tenants, no
// query may name either index in an index hint.
@Index(
	'item_warehouse_onHand_warehouse_item',
	['quantityOnHand', 'fkWarehouseId', 'fkItemId'],
	{},
)
@Index(
	'item_warehouse_itemExternalId_deletedDate',
	['itemExternalId', 'deletedDate'],
	{},
)
@Entity('item_warehouse')
export class ItemWarehouseEntity extends BaseEntity {
	@Column('varchar', { name: 'externalId', nullable: true, length: 64 })
	public externalId!: string | null;

	@Column('varchar', { name: 'itemExternalId', nullable: true, length: 64 })
	public itemExternalId!: string | null;

	@Column('int', { name: 'fk_item__id', unsigned: true })
	public fkItemId!: number;

	@Column('int', { name: 'fk_warehouse__id', unsigned: true })
	public fkWarehouseId!: number;

	@Column('datetime', { name: 'externalModifiedDatetime', nullable: true })
	public externalModifiedDatetime!: Date | null;

	@Column('int', { name: 'quantityOnHand', nullable: true })
	public quantityOnHand!: number | null;

	@Column('int', { name: 'quantityOnOrder', nullable: true })
	public quantityOnOrder!: number | null;

	/** Quantity reserved by submitted sales orders but not yet picked. Available qty = quantityOnHand - quantityReserved. */
	@Column('int', { name: 'quantityReserved', nullable: true, default: 0 })
	public quantityReserved!: number | null;

	@Column('date', { name: 'soonestExpirationDate', nullable: true })
	public soonestExpirationDate!: string | null;

	@Column('date', { name: 'soonestArrivalDate', nullable: true })
	public soonestArrivalDate!: string | null;

	@Column('decimal', { name: 'price', nullable: true, precision: 13, scale: 2 })
	public price!: string | null;

	@Column('decimal', {
		name: 'minimumPrice',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public minimumPrice!: string | null;

	@Column('decimal', {
		name: 'acquisitionCost',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public acquisitionCost!: string | null;

	@Column('decimal', {
		name: 'sellCost',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public sellCost!: string | null;

	@ManyToOne(() => ItemEntity, (item) => item.itemWarehouses, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_item__id', referencedColumnName: 'id' }])
	public item!: ItemEntity;

	@ManyToOne(() => WarehouseEntity, (warehouse) => warehouse.itemWarehouses, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_warehouse__id', referencedColumnName: 'id' }])
	public warehouse!: WarehouseEntity;
}
